import { useEffect } from 'react';

const PLATFORM_SHELL_SELECTOR = '[data-testid="ai-shell-panel"]';
const TQ_SHELL_BOUNDARY = '[data-tq-shell]';
const CSS_VAR = '--platform-shell-width';

/**
 * Syncs the platform AI shell panel width to a CSS variable on :root.
 * Uses ResizeObserver directly (no React state) so the CSS variable updates
 * on every animation frame without a render cycle, eliminating layout lag.
 *
 * Apply via: style={{ marginRight: 'var(--platform-shell-width, 0px)' }}
 */
export function useSyncAIShellLayout(): void {
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const attachResize = (el: Element) => {
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(([entry]) => {
        document.documentElement.style.setProperty(CSS_VAR, `${entry.contentRect.width}px`);
      });
      resizeObserver.observe(el);
      document.documentElement.style.setProperty(CSS_VAR, `${(el as HTMLElement).offsetWidth}px`);
    };

    const findPlatformPanel = () => {
      const el = document.querySelector(PLATFORM_SHELL_SELECTOR);
      return el && !el.closest(TQ_SHELL_BOUNDARY) ? el : null;
    };

    const mutationObserver = new MutationObserver(() => {
      const el = findPlatformPanel();
      if (el) {
        attachResize(el);
      } else {
        resizeObserver?.disconnect();
        resizeObserver = null;
        document.documentElement.style.setProperty(CSS_VAR, '0px');
      }
    });

    const existing = findPlatformPanel();
    if (existing) attachResize(existing);

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      document.documentElement.style.removeProperty(CSS_VAR);
    };
  }, []);
}
